/**
 * キューごとのジョブ名レジストリ（`docs/ARCHITECTURE.md` §4.4）。
 *
 * **なぜ必要か**: 過去に `provision` / `dns.verify` / `email.send` の 3 系統で
 * enqueue 側と handlers 側の名前が食い違い、dev でプロビジョニングジョブが
 * 1 件も実行されず、セキュリティ通知メールが 1 通も送られていなかった。
 * job-runner は未登録名を throw するので、API は 202 を返すのにワーカーは
 * 5 回失敗して dead-letter に落ちる。**押した本人は成功したと誤認する**。
 *
 * 名前を 1 か所に集めて型で縛れば、typo と改名漏れはコンパイル時に落ちる。
 * ここに無い名前は `enqueue()` の型が受け付けない。逆にここにあってワーカーに
 * ハンドラが無い名前は `apps/worker/src/workers/job-names.test.ts` が落とす。
 *
 * **追加するときは必ず両方**（この表とワーカーの handlers）を同時に触ること。
 */
import { JOB_KIND } from './enums.js';
import type { QueueName } from './queues.js';

export const JOB_NAMES = {
  /**
   * 設計 02 §6.1 の名前に加えて、**`JOB_KIND` そのもの**も受ける。
   * `apps/api` の `createProvisioningJob()` は `jobName: input.kind` で積むため
   * （両方受ける理由はワーカー側 `provision.ts` の注記を参照）。
   */
  provision: [
    'org.bootstrap',
    'org.migrate',
    'org.purge',
    'domain.provision',
    'mailbox.provision',
    'mailbox.purge',
    'provision.run',
    'provision.sweep',
    ...JOB_KIND,
  ],
  'state-publish': ['state.bump', 'state.notify', 'state.publish-all'],
  'node-command': ['command.dispatch', 'command.timeout-sweep'],
  'dns-verify': [
    'domain.verify',
    'dns.verify',
    'domain.reverify-sweep',
    /*
     * カスタムホスト名（`webmail.<顧客ドメイン>` / 配信リンク）の CNAME 検証。
     * **契約者は状態を `active` にできない**ので、確認して昇格させるのはこの
     * ジョブだけ。自己申告で active にできると、DNS も証明書も無い名前が
     * 配られて TLS が失敗する。
     */
    'dns.verify-custom-hostname',
    /*
     * R0 §2.3「PTR / FCrDNS 検証が未実装」。ノードの出口 IP について
     * 逆引き → 前方引き → hostname 一致を検査する。ドメインの検証とは
     * 対象（ノード / 契約者のドメイン）も頻度も違うが、3 リゾルバ多数決の
     * 実装をそのまま使えるので同じキューに置く。
     */
    'dns.verify-node',
    'dns.verify-node-sweep',
    /*
     * DKIM 鍵のローテーション（R122 / A171 / A210 / A252）。
     * `dkim_keys.rotate_after` は 0000 の時点から書かれていたのに
     * **読むコードが 1 行も無く**、鍵は無期限に固定されていた。
     *
     * `dns-verify` キューに置くのは、遷移の判定材料が
     * 「新セレクタの TXT が観測できたか」だけで、その観測を
     * このキューが `domain_dns_records` に書いているため。
     */
    'dkim.rotate',
    'dkim.rotate-sweep',
  ],
  acme: ['cert.issue', 'cert.renew', 'cert.renew-sweep', 'acme.dns_challenge'],
  'usage-rollup': [
    'usage.hourly',
    'usage.daily',
    'usage.month-close',
    /*
     * メールログ（`mail_log_entries`・R135）の保持削除。
     * `maintenance` の `sweep.retention` に相乗りさせていないのは、
     * 配送ログだけ行数の桁が違い（1 通 1 行）、他の表の掃除と
     * 同じ 1 回の実行に入れると「どこで詰まったか」が見えなくなるため。
     */
    'maillog.retention',
  ],
  billing: [
    'invoice.generate',
    'invoice.charge',
    'invoice.dunning',
    'invoice.pdf',
    'invoice.pdf-sweep',
    'subscription.sync',
    'subscription.trial-close',
    'billing.retry_payment',
    'billing.close_all',
    'billing.close_one',
    'sla.monthly-credit',
    'contract.terminate-sweep',
    /*
     * 決済・契約ライフサイクル（W-BILLING 2/3）。
     *
     * `billing.stripe-event`
     *   Stripe Webhook の本処理。**API は署名検証だけして即 200 を返し、
     *   DB への反映はここでやる**（REQUIREMENTS.md §8.3「即 200 → BullMQ へ」）。
     *   API 側で DB を触ると、DB が詰まった瞬間に Stripe の 10 秒
     *   タイムアウトに掛かって再送の嵐になる。
     * `billing.settle`
     *   入金の反映（請求書の消し込み）と、未納が解消した組織の凍結解除。
     *   manual（ops の入金登録）と stripe（Webhook）の両方から呼ぶ。
     * `billing.refund`
     *   返金の実行（プロバイダ呼び出し）。記録だけなら API で完結するが、
     *   カード返金は外部 I/O なのでワーカーに置く。
     * `invoice.credit-note`
     *   赤伝（適格返還請求書）の発行。
     * `invoice.write-off`
     *   長期未納の貸倒（uncollectible）と契約解除。**督促の終端**。
     * `billing.payment-method-session`
     *   決済手段の登録セッション（Stripe Checkout `mode:'setup'`）生成。
     * `contract.terminate`
     *   運営による契約解除（`POST /ops/orgs/:orgId/terminate`）。
     *   `terminate_after` を立てる処理は `beginTermination` 1 か所に集める。
     *   API から直接書くと、通知・`contract_events`・猶予日数の規約が
     *   2 か所に分かれて必ずズレる。**実削除はしない**（猶予後に
     *   `contract.terminate-sweep` が行う）。
     */
    'billing.stripe-event',
    'billing.settle',
    'billing.refund',
    'invoice.credit-note',
    'invoice.write-off',
    'billing.payment-method-session',
    'contract.terminate',
    /*
     * 契約ライフサイクルの残り（W-BILLING 3/3）。
     *
     * `contract.revoke-termination`
     *   解約の撤回。`terminate_after` を**立てる**のが `beginTermination` の
     *   1 か所だけなのと同じ理由で、**外す**のもここ 1 か所に集める。
     *   API から直接 NULL にすると、メールボックスの復帰と契約の再開が
     *   抜けて「解約は取り消したのに送信できない」が残る。
     * `contract.purge-org`
     *   解約確定（`closed`）から猶予期間の後に、契約者が預けた
     *   **第三者の個人データ**（宛先名簿・展開済み宛先・S3 の配信本文と
     *   アセット）を消す。`contract.terminate-sweep` が消すのは
     *   メールボックス（Maildir）だけで、名簿は無期限に残っていた。
     * `sla.uptime-rollup`
     *   `node_heartbeats`（14 日保持）を消される前に日次へ畳む。
     *   月次 SLA は `node_uptime_daily` だけを読む。**この順序が崩れると
     *   「無停止なのに稼働率 45%」で全組織にクレジットが起票される。**
     * `export.run` / `export.sweep`
     *   契約者のデータエクスポート（`export_jobs`）の実行と回収。
     *   **新しいキューを作らず billing に相乗りさせる**のは、キューを増やすと
     *   `apps/worker/src/workers/index.ts` への登録漏れという面が増えるため
     *   （`maintenance` に CMS の定期ジョブを相乗りさせているのと同じ判断）。
     *   エクスポートは開示請求と解約時のデータ持ち出しのための機能なので、
     *   契約ライフサイクルと同じキューにあること自体は不自然ではない。
     */
    'contract.revoke-termination',
    'contract.purge-org',
    'sla.uptime-rollup',
    'export.run',
    'export.sweep',
    /*
     * `sla.stale-proposed`（W-BILLING2）
     *   起票済み（`proposed`）のまま承認も却下もされずに滞留している SLA
     *   クレジットを運営へ再通知する。掲示（/legal/sla 第 5 条）は
     *   「当社が検知した場合は申告がなくても当社が起票する」と約束している
     *   のに、承認するのは人手なので、**承認漏れがあると検知済みの停止に
     *   クレジットが付かないまま月次締めが通る**（`proposed` は契約者面に
     *   出ないので、契約者からは何も見えない）。滞留を可視化する。
     */
    'sla.stale-proposed',
    /*
     * `billing.price-schedule`（W-BILLING2 2/2・A241 の残り）
     *   予約された単価の変更を「適用日が来たら焼き付ける」日次ジョブ。
     *   値上げは即時に効かせられない（利用規約 第 11 条 = 民法 548 条の 4 で、
     *   不利益変更は効力発生日の 30 日前までに通知すると掲示している）。
     *   **周知していない予約は適用しない**。適用してしまうと、掲示した手続きを
     *   当社自身が守っていないことになる。
     * `billing.overage-notice`（A72）
     *   当月の従量課金（超過分）が積み上がっていることを、請求書が出る前に
     *   契約者へ知らせる。単価を掲示しただけでは「自分がいくらになるか」は
     *   分からないので、金額が動いたことに気付けるようにする。
     */
    'billing.price-schedule',
    'billing.overage-notice',
  ],
  'update-rollout': ['rollout.tick', 'rollout.evaluate-gate', 'rollout.rollback', 'rollout.sweep'],
  'aurora-sync': [
    'aurora.tenant.upsert',
    'aurora.mailaccount.upsert',
    'aurora.mailaccount.disable',
    'aurora.tenant.suspend',
    /**
     * 解約後の Webメールテナント削除（R113）。**削除猶予が明けてから**積む。
     * 積むのは契約終了の掃除（`billing` の解約処理・組織 purge）側。
     */
    'aurora.tenant.purge',
    'aurora.reconcile',
    'aurora.reconcile-sweep',
    'webmail.reprovision',
    'webmail.password_sync',
    /*
     * 組織の AI ポリシー（`org_ai_policies`）を aurora-mail のテナント設定へ
     * 反映する。**無効化もこのジョブで行う**（provider='none' を送る）。
     * 向こうに「無効」フラグが無いので、積まなければ止まらない。
     */
    'aurora.llm-provider.sync',
    /*
     * 同期に失敗したまま放置された組織を拾い直す掃除。
     * ジョブ 1 本が失敗して終わると、契約者の画面には「設定済み」と出るのに
     * Webメールの挙動は前のままになる（＝いちばん気付けない壊れ方）。
     */
    'aurora.llm-provider.sweep',
    /*
     * aurora-mail の `ai_inference_logs` を 1 日ぶんずつ取り直して
     * `ai_usage_daily` / `usage_samples` へ落とす（組織ごと）。
     * **増分カーソルで足し込まない**（境界の二重計上が請求に出て、
     * しかもあとから検算できない）。
     */
    'ai.usage.ingest',
    /** 取り込み対象の組織を撒く。月替わりで `capped_at` を戻すのもここ。 */
    'ai.usage.sweep',
    /*
     * 接続先（`ai_provider_endpoints`）の疎通。
     * 落ちている接続先を候補から外すのはこのジョブだけなので、
     * 止まると**落ちた先へ本文を送り続ける**（Webメール側の AI が全滅する）。
     */
    'ai.endpoint.healthcheck',
    /*
     * プライベートメールアドレス（使い捨て転送アドレス）を aurora-mail の
     * `identities`（"Send mail as"）へ同期する。
     *
     * **これが動かないと機能の意味が半分消える。** 発行したアドレス宛に
     * 届いたメールに Webメールから返信したとき、identity が無ければ From は
     * 本来のアドレスになり、**その瞬間に相手へ本来のアドレスが渡る**。
     * 受信と転送だけが動いていても「渡さない」という目的は達成できない。
     *
     * 1 メールボックス単位の**全量同期**（差分ではない）。何度流しても
     * 同じ状態に収束するので、取りこぼしても次の変更でまとめて直る。
     */
    'aurora.private-address.sync',
  ],
  notify: ['notify.email', 'email.send', 'notify.webhook', 'notify.slack', 'notify.expire-sweep'],
  'abuse-scan': [
    'abuse.rate-check',
    'abuse.pattern-scan',
    /*
     * 対応期限の督促（R45 / A97 / A134 / A167）。**受け付けただけで
     * 誰も見に来ない**のがいちばん多い失敗なので、期限を過ぎた通報・
     * 問い合わせ・権利行使要求・漏えい報告を鳴らす。
     * 新しいキューを作らず `abuse-scan` に相乗りさせているのは、
     * キューが増えるほど worker の登録漏れが起きる面が増えるため。
     */
    /*
     * 苦情率（Gmail / Yahoo! の一括送信者要件 0.3%）の監視。R44。
     * FBL の取り込みだけでは「記録はされているが誰も気付かない」で終わる。
     */
    'abuse.complaint-rate',
    /*
     * レピュテーション系サービス（Postmaster Tools / SNDS / JMRP / DQS）の
     * 登録状況の点検。R48。登録は人手なので、**登録されていないこと自体を
     * 機械が鳴らす**以外に気付く方法が無い。
     */
    'reputation.review-sweep',
    'abuse.sla-sweep',
    'support.sla-sweep',
  ],
  backup: [
    'backup.verify',
    'restore.request',
    /*
     * R0 §2.4。`restore.request` は「要求を受け付けた」だけで終わるので、
     * そこから先（対象ノードへの指示・進捗の追跡・期限超過の検知）を分ける。
     *   restore.dispatch … 受理済みの復元要求をノードへ指示する
     *   restore.sweep    … 動いていない復元要求の督促と期限切れ
     *   backup.cp-verify … **CP 自身の** Postgres バックアップの健全性確認
     *                      （既存の backup.verify はノードしか見ていない）
     */
    'restore.dispatch',
    'restore.sweep',
    'backup.cp-verify',
    /*
     * `backup.retention` = **ノード側 restic リポジトリの保持整理の確認**。
     *
     * 設計は「ノードには消させない、CP が消す」。ノードが侵害されると
     * 攻撃者はノード上の S3 キーと restic パスフレーズを両方持つので、
     * `forget --prune` ひとつでバックアップごと消せてしまう（ランサムウェアの
     * 典型的な手口）。だからノードへ配る鍵は append-only にしてある。
     *
     * **実際に消すのはワーカーではない。** 削除権限のある鍵を
     * アプリケーションプロセスに持たせないため、実行主体は cp-backup
     * コンテナの週次 cron（`deploy/vps/node-retention.sh`）。
     * このジョブはその実施記録（`system_settings.node_backup_retention_status`）を
     * 見て、**止まっていたら鳴らす**役割を持つ。
     */
    'backup.retention',
    /*
     * `backup.drill-reminder` = **人手の復旧演習の督促**（R41 / A87 / R37）。
     *
     * REQUIREMENTS §9.2 の RPO / RTO は要件本文自身が「机上計算であり
     * 演習で実測するまで妥当性は不明」と書いている。実測を残す台帳
     * （`dr_drills`、0038）を作っただけでは誰も書かないので、
     * **書かれていないことを月次で鳴らす**。
     *
     * 機械の月次復元テスト（`cp-backup.sh verify-restore`）とは別建て。
     * 機械が確かめているのは「CP のダンプが使い捨て DB へ戻ること」だけで、
     * ノード全損からの再構築は人にしか確かめられない。
     */
    'backup.drill-reminder',
  ],
  // `audit.worm-retry` = WORM への PUT に失敗したチェックポイントの再送と、
  // 未アンカーが溜まっていることの通知（R0 §2.5）。
  'audit-archive': ['audit.checkpoint', 'audit.verify-chain', 'audit.worm-retry'],
  maintenance: [
    'sweep.outbox',
    'sweep.stale-nodes',
    'sweep.sessions',
    'sweep.retention',
    'sweep.partitions',
    'sweep.mailbox-purge',
    /*
     * `sweep.mailbox-quota`（R126 / R127）… 使用率 80% / 95% の警告と
     * `over_quota` への遷移。**`over_quota` へ遷移させるコードは
     * 1 行も存在しなかった**ので、`mailbox_status` の 4 値のうち 1 値が
     * 永久に発生せず、運営コンソールの「容量超過」は常に 0 件だった。
     */
    'sweep.mailbox-quota',
    'rotate.node-secrets',
    /*
     * R0 §2.4「気づける」。どちらも `maintenance` に相乗りさせる
     * （新しいキューを作ると worker の登録漏れが起きる面が増える）。
     *   sweep.cert-expiry     … 「更新できていないまま期限が近い」の独立検知
     *   sweep.node-thresholds … ディスク逼迫・キュー滞留の閾値判定
     */
    'sweep.cert-expiry',
    'sweep.node-thresholds',
    /*
     * 「壊れたことに気づける」の 2 本目（A41 / A231）。
     *   sweep.dead-letter … DLQ の滞留件数を鳴らし、保持期間を過ぎた分を捨てる。
     *                       DLQ は**意図的にコンシューマを付けない**ので、
     *                       件数を外へ出す経路が無いと誰も見に来ない。
     *   sweep.job-ledger  … 定期ジョブの実行台帳を見て「動いていない」を検知する。
     *                       ワーカーごと死んでいる場合はこの sweep も動かないので、
     *                       `worker_heartbeats` を見る API 側と両方が要る。
     */
    'sweep.dead-letter',
    'sweep.job-ledger',
    /*
     * `sweep.cp-thresholds` … **CP 自身の**容量（DB サイズ・Redis メモリ）の
     * 閾値判定（A277）。ノードには `sweep.node-thresholds` があるのに、
     * CP には同等のものが無かった。CP の Postgres が書けなくなると、
     * その瞬間から通知も監査も残せなくなる（＝壊れたことを記録できない壊れ方）。
     */
    'sweep.cp-thresholds',
    /* content-schedule.ts / asset-gc.ts / content-sweep.ts が相乗りする分 */
    'sweep.content-schedules',
    'sweep.unused-assets',
    'sweep.asset-purge',
    'sweep.content-revisions',
    'sweep.content-orphans',
    /**
     * 未確認の申込（`signup_submissions`）の生アドレスを 72 時間で NULL 化する。
     * ここが無いと、確認されなかった第三者のアドレスが
     * list-bombing の副産物として永久に残る（D23）。
     */
    'sweep.signup-submissions',
    /*
     * 画像プロキシ（受信者のプライバシー保護）の定期処理。
     * こちらも新しいキューは作らず `maintenance` に相乗りさせる。
     *   sweep.image-proxy-cache  … 期限切れキャッシュの回収（S3 → DB の順）
     *   sweep.image-proxy-egress … **自動無効化した出口 IP の復帰**。
     *     戻さないと、上流の一時障害でプールが片道に痩せて最後は 0 本になる。
     *     0 本でも取得自体は既定の出口から成功するので、
     *     **ローテーションが黙って無効化された状態**に誰も気付けない。
     */
    'sweep.image-proxy-cache',
    'sweep.image-proxy-egress',
  ],
  'content-build': ['content.render', 'asset.derive', 'content.reindex', 'link.check'],
  webhook: ['webhook.fanout', 'webhook.deliver', 'sweep.webhook-retry', 'sweep.webhook-log'],
  'bulk-plan': ['campaign.expand'],
  'bulk-dispatch': [
    'campaign.start',
    'campaign.resume',
    'campaign.complete',
    'campaign.release_expired',
    'sweep.campaign-schedule',
    /**
     * 「申し出たのに止まっていない」配信停止の回収。
     * `unsubscribe_records.applied_at` が NULL の行を拾い直す
     * （公開面の `POST /public/u/:ref` は DB へ書けなくても 200 を返すので、
     * 回収経路が無いと解除が静かに消える。特定電子メール法 3 条 3 項）。
     */
    'sweep.unsubscribe-pending',
    /**
     * 送信元ストリーム（`sending_streams`）を `verified` へ進める唯一の経路。
     * これが無かった間、`preflight()` が要求する `verified` を書くコードが
     * 1 行も存在せず、**全キャンペーンが `stream_verifying` で恒久停止**していた。
     *   stream.verify          … 1 本だけ検証（作成時・契約者の依頼時）
     *   sweep.sending-streams  … 定期の見直し（DNS が後から壊れたことの検知）
     */
    'stream.verify',
    'sweep.sending-streams',
    /**
     * 宛先 CSV の取り込み（`contact_imports`）。表・enum・RLS は 0008/0009 から
     * 在るのに実装が 0 本で、10 万件の名簿を 1 件ずつの API でしか入れられなかった。
     *   contact.import-validate … S3 の CSV を検査して preview_ready にする
     *   contact.import-apply    … 検査済みの内容を contacts / 同意証跡へ反映する
     */
    'contact.import-validate',
    'contact.import-apply',
  ],
  /**
   * 配送イベントの集計・安全弁・保持期間（`apps/worker/src/workers/bulk-events.ts`）。
   *
   * 長らく**空配列**だった（名前だけ先に置くと「積めるのに誰も処理しない」を
   * 作るため、実装と同時に足す約束になっている）。
   *   events.ingest             … 取り込み直後の再集計と判定（API が outbox 経由で積む）
   *   campaign.guard            … サーキットブレーカ（苦情率 0.1% で pause / 0.3% で halt）
   *   sweep.campaign-guard      … 配信中のキャンペーンの定期監視
   *   sweep.campaign-partitions … `campaign_events` の月次パーティション先行作成
   *   sweep.campaign-clicks     … クリック総数を `campaigns.stats.click` へ焼く
   *                               （`campaign_events` は 90 日で落ちるので、
   *                                 焼かないと 91 日目のレポートが「0」と嘘をつく）
   *   sweep.campaign-retention  … 配送ログ 90 日保持の実行
   *   sweep.outbound-pools      … RBL 掲載 IP の自動退避と、出口を失った
   *                               送信ストリームの再割り当て（PD §5.1）
   */
  'bulk-events': [
    'events.ingest',
    'campaign.guard',
    'sweep.campaign-guard',
    'sweep.campaign-partitions',
    'sweep.campaign-clicks',
    'sweep.campaign-retention',
    'sweep.outbound-pools',
  ],
  /**
   * R0 §2.3。到達性の定期処理。
   *   warmup.advance   … `ip_warmup_schedules.current_day` を進めて上限を上げる
   *   warmup.ensure    … enroll 済みノードにウォームアップ行が無ければ起票する
   *   rbl.sweep        … 掲載の解除検知と、掲載が続いていることの再通知
   *   spf.publish      … `_spf.<service>` の「あるべき値」を組み立てて実測と突合
   *   suppression.expire … 期限切れのソフトバウンス抑止を解除する
   */
  deliverability: [
    'warmup.advance',
    'warmup.ensure',
    'rbl.sweep',
    'spf.publish',
    'suppression.expire',
  ],
  /** コンシューマを付けない滞留先。積むのは job-runner だけ。 */
  'dead-letter': ['dead'],
} as const satisfies Record<QueueName, readonly string[]>;

/** キュー `Q` に積んでよいジョブ名。 */
export type JobNameOf<Q extends QueueName> = (typeof JOB_NAMES)[Q][number];

/** 全キューを通じたジョブ名の和。 */
export type AnyJobName = { [Q in QueueName]: JobNameOf<Q> }[QueueName];

export function isJobNameOf(queue: QueueName, name: string): boolean {
  return (JOB_NAMES[queue] as readonly string[]).includes(name);
}
